import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader } from '../../../shared/components/Loader';
import { dashboardService } from '../../../shared/services/dashboardService';
import { DashboardData, DashboardRecentInvoice } from '../../../shared/types/dashboard';

const formatCurrency = (amount?: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount ?? 0);

const formatDate = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
};

const defaultDashboardData: DashboardData = {
    invoices: {
        total: 0,
        pending: 0,
        paid: 0,
        movedToLoss: 0,
        movedToTds: 0,
        totalAmount: 0,
        totalPendingAmount: 0,
        totalReceivedAmount: 0,
    },
    losses: { total: 0, totalAmount: 0 },
    tds: { total: 0, totalAmount: 0 },
    clients: { total: 0, active: 0 },
    monthlyRevenue: [],
    recentInvoices: [],
};

export const Dashboard: React.FC = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [dashboardData, setDashboardData] = useState<DashboardData>(defaultDashboardData);

    const fetchDashboard = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await dashboardService.getDashboard();
            setDashboardData(response?.data || defaultDashboardData);
        } catch (err: any) {
            setDashboardData(defaultDashboardData);
            setError(err?.response?.data?.message || err?.message || 'Error loading dashboard data');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    const recentInvoices = useMemo(() => (Array.isArray(dashboardData.recentInvoices) ? dashboardData.recentInvoices : []), [dashboardData.recentInvoices]);

    return (
        <div className="flex flex-col w-full min-h-full bg-white">
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <div className="flex-1 min-w-0">
                    <div className="mt-2">
                        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-7 sm:leading-10 truncate text-gray-900">Dashboard</h2>
                    </div>
                </div>
            </div>

            <div className="flex flex-1 p-6 sm:p-10 overflow-y-auto">
                <div className="flex flex-col w-full gap-6">
                    {isLoading ? (
                        <div className="flex items-center justify-center min-h-[300px]">
                            <Loader />
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3">
                                    {error}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                                <div className="rounded-lg border border-gray-200 p-4 bg-white">
                                    <p className="text-sm text-gray-500">Total Invoices</p>
                                    <p className="text-2xl font-bold text-gray-900 mt-1">{dashboardData.invoices.total}</p>
                                    <p className="text-sm text-gray-600 mt-1">Pending: {dashboardData.invoices.pending} | Paid: {dashboardData.invoices.paid}</p>
                                </div>
                                <div className="rounded-lg border border-gray-200 p-4 bg-white">
                                    <p className="text-sm text-gray-500">Invoice Amount</p>
                                    <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(dashboardData.invoices.totalAmount)}</p>
                                    <p className="text-sm text-gray-600 mt-1">Pending: {formatCurrency(dashboardData.invoices.totalPendingAmount)}</p>
                                </div>
                                <div className="rounded-lg border border-gray-200 p-4 bg-white">
                                    <p className="text-sm text-gray-500">Received Amount</p>
                                    <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(dashboardData.invoices.totalReceivedAmount)}</p>
                                    <p className="text-sm text-gray-600 mt-1">Moved to Loss: {dashboardData.invoices.movedToLoss} | TDS: {dashboardData.invoices.movedToTds}</p>
                                </div>
                                <div className="rounded-lg border border-gray-200 p-4 bg-white">
                                    <p className="text-sm text-gray-500">Clients</p>
                                    <p className="text-2xl font-bold text-gray-900 mt-1">{dashboardData.clients.total}</p>
                                    <p className="text-sm text-gray-600 mt-1">Active: {dashboardData.clients.active}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                                <div className="rounded-lg border border-gray-200 p-4 bg-white">
                                    <p className="text-sm text-gray-500">Losses</p>
                                    <p className="text-2xl font-bold text-red-600 mt-1">{dashboardData.losses.total}</p>
                                    <p className="text-sm text-gray-600 mt-1">Amount: {formatCurrency(dashboardData.losses.totalAmount)}</p>
                                </div>
                                <div className="rounded-lg border border-gray-200 p-4 bg-white">
                                    <p className="text-sm text-gray-500">TDS</p>
                                    <p className="text-2xl font-bold text-amber-600 mt-1">{dashboardData.tds.total}</p>
                                    <p className="text-sm text-gray-600 mt-1">Amount: {formatCurrency(dashboardData.tds.totalAmount)}</p>
                                </div>
                                <div className="rounded-lg border border-gray-200 p-4 bg-white">
                                    <p className="text-sm text-gray-500">Collection Ratio</p>
                                    <p className="text-2xl font-bold text-blue-600 mt-1">
                                        {dashboardData.invoices.totalAmount > 0
                                            ? `${((dashboardData.invoices.totalReceivedAmount / dashboardData.invoices.totalAmount) * 100).toFixed(1)}%`
                                            : '0.0%'}
                                    </p>
                                    <p className="text-sm text-gray-600 mt-1">Based on invoice total vs received amount</p>
                                </div>
                            </div>

                            <div className="rounded-lg border border-gray-200 bg-white p-4">
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Monthly Revenue</h3>
                                {dashboardData.monthlyRevenue.length === 0 ? (
                                    <p className="text-sm text-gray-500">No monthly revenue data found.</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="bg-gray-50">
                                                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Month</th>
                                                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Total Amount</th>
                                                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Received Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dashboardData.monthlyRevenue.map((item) => (
                                                    <tr key={item.month} className="border-t border-gray-100">
                                                        <td className="py-2 px-3 text-gray-800">{item.month}</td>
                                                        <td className="py-2 px-3 text-gray-800">{formatCurrency(item.totalAmount)}</td>
                                                        <td className="py-2 px-3 text-gray-800">{formatCurrency(item.receivedAmount)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className="rounded-lg border border-gray-200 bg-white p-4">
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Recent Invoices</h3>
                                {recentInvoices.length === 0 ? (
                                    <p className="text-sm text-gray-500">No recent invoices found.</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="bg-gray-50">
                                                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Invoice #</th>
                                                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Client</th>
                                                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Date</th>
                                                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Total</th>
                                                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Pending</th>
                                                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recentInvoices.map((invoice: DashboardRecentInvoice, index: number) => (
                                                    <tr key={invoice._id || invoice.id || `${invoice.invoiceNumber || 'invoice'}-${index}`} className="border-t border-gray-100">
                                                        <td className="py-2 px-3 text-gray-800">{invoice.invoiceNumber || '-'}</td>
                                                        <td className="py-2 px-3 text-gray-800">{invoice.clientCompanyName || '-'}</td>
                                                        <td className="py-2 px-3 text-gray-800">{formatDate(invoice.invoiceDate)}</td>
                                                        <td className="py-2 px-3 text-gray-800">{formatCurrency(invoice.totalAmount)}</td>
                                                        <td className="py-2 px-3 text-gray-800">{formatCurrency(invoice.totalPendingAmount)}</td>
                                                        <td className="py-2 px-3 text-gray-800">{invoice.status || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
